'use client'
import { ControllerRenderProps, Path } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormItem,
  FormMessage,
} from '@/components/ui/form/form'
import { Label } from '@radix-ui/react-label'
import { Textarea } from '../textarea/textarea'
import type { TypeOf, ZodSchema } from 'zod'

export const TextAreaInput = ({
  field,
  label,
  readOnly,
  readFormatter,
  description,
  maxLength,
  className,
  transactionName,
}: {
  field: ControllerRenderProps
  label: string
  readOnly?: boolean
  readFormatter?: (value: string) => string | false
  description?: (value: string) => string | false
  maxLength?: number
  className?: string
  /**
   * Transaction name for RUM monitoring.
   * Required for observability - tracks user interactions in Elastic APM.
   * Format: area.page.field_name (e.g., "admin.login.email")
   */
  transactionName: string
}) => {
  const { description: descriptionValue } = getContents(
    field,
    readFormatter,
    description
  )
  return (
    <FormItem className="relative">
      <FormControl>
        <Textarea
          maxLength={maxLength}
          className={className}
          {...field}
          readOnly={readOnly}
          transactionName={transactionName}
        />
      </FormControl>
      <Label
        className={'absolute left-3 !-mt-[10px] bg-white px-1 text-xs'}
        htmlFor={field.name}
      >
        {label}
      </Label>
      <FormDescription className="text-end text-muted-foreground">
        {descriptionValue}
      </FormDescription>
      <FormMessage />
    </FormItem>
  )
}

const getContents = <T extends ZodSchema, K extends Path<TypeOf<T>>>(
  field?: ControllerRenderProps<TypeOf<T>, K>,
  readFormatter?: (value: string) => string | false,
  description?: (value: string) => string | false
): { name: string; value: string; description: string } => {
  if (!field) {
    throw new Error(
      'Either Field or Static Content is Required When in Read Mode'
    )
  }
  const formattedValue = readFormatter?.(field.value) || field.value
  const descriptionValue = description?.(field.value) || ''

  return {
    name: field.name,
    value: formattedValue,
    description: descriptionValue,
  }
}
